/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useForm, useFieldArray } from 'react-hook-form';
import { DialogButton, DialogTitleClose, TextfieldSelect } from 'components';
import { AutoCompleteLoadData } from 'components/AutoCompleteLoadData';
import {
  CatalogProduct,
  PatientFragment,
  PlaceOrderInput,
  PlaceOrderLineInput,
  PlaceOrderMutation,
  PlaceOrderMutationVariables,
  SearchCatalogProductsQuery,
  SearchCatalogProductsQueryVariables,
  ShippingMode,
  SurveyResultEmbeddedFragment,
  SftpSettingsQuery,
  SftpSettingsQueryVariables,
  EdiOrdersQuery,
  EdiOrdersQueryVariables,
} from 'types.d';
import { useMutationCustom, useQueryCustom, useUpdateMeClient } from 'hooks';
import {
  EDI_INBOUND_DOCS,
  EDI_ORDERS,
  PLACE_ORDER,
  SEARCH_CATALOG_PRODUCTS,
  SFTP_SETTINGS,
} from 'gql';
import { useLazyQuery } from '@apollo/react-hooks';
import { getDataOwner } from 'share/utils';
import OrderStatusDialog from './OrderStatusDialog';

type CatalogOption = CatalogProduct & {
  label: string;
  optionId: string; // unique per itemNumber + UOM
};

type OrderLineForm = {
  supplierItem: string;
  buyerItem?: string;
  requestedEach: number | string;
  catalogSelection?: CatalogOption | string | null;
};

type OrderFormValues = {
  shippingMode: ShippingMode;
  shipTo: {
    name: string;
    account?: string;
    addr1?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    email?: string;
  };
  lines: OrderLineForm[];
  clinicShipToCode?: string;
};

type InboundDocType = 'PO_ACK_855' | 'ASN_856' | 'INVOICE_810';

type InboundDoc = {
  _id: string;
  type: InboundDocType | string;
  filename: string;
  linkedPo?: string | null;
  linkedPos?: Array<string | null> | null;
  error?: string | null;
  parsedJson?: string | null;
  createdAt?: string | null;
};

type InboundDocsQueryResult = {
  ediInboundDocs?: InboundDoc[] | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  patient?: PatientFragment;
  surveyResult?: SurveyResultEmbeddedFragment;
  surveyName?: string;
  onOrderPlaced?: () => void;
};

const normalizeValue = (value?: string | null) => {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const defaultLine: OrderLineForm = {
  supplierItem: '',
  buyerItem: '',
  requestedEach: 1,
  catalogSelection: null,
};

const PlaceOrderDialog: React.FC<Props> = ({
  open,
  onClose,
  patient,
  surveyResult,
  surveyName,
  onOrderPlaced,
}) => {
  const { meClient } = useUpdateMeClient();
  const patientOwner = patient?.owner;
  const organizationId =
    meClient?.organizationId ||
    ((patientOwner?.organization as any)?.idOrg as string | undefined) ||
    ((patientOwner?.organization as any)?._id as string | undefined) ||
    '';

  const patientName = useMemo(() => {
    if (!patientOwner) {
      return '';
    }
    const first = patientOwner.firstName || '';
    const middle = patientOwner.middleName || '';
    const last = patientOwner.lastName || '';
    return [first, middle, last].filter(Boolean).join(' ').trim();
  }, [patientOwner]);

  const patientEmail = useMemo(() => {
    const email = getDataOwner('email', patientOwner);
    return email === 'N/A' ? '' : email || '';
  }, [patientOwner]);

  const patientPhone = useMemo(() => {
    if (!patientOwner?.phones || patientOwner.phones.length === 0) {
      return '';
    }
    const phoneObj = patientOwner.phones[0] as { phone?: string };
    return phoneObj?.phone || '';
  }, [patientOwner]);

  const defaultValues = useMemo<OrderFormValues>(
    () => ({
      shippingMode: ShippingMode.Patient,
      shipTo: {
        name: patientName,
        account: '',
        addr1: patientOwner?.address || '',
        city: '',
        state: '',
        zip: '',
        phone: patientPhone,
        email: patientEmail,
      },
      lines: [{ ...defaultLine }],
      clinicShipToCode: '',
    }),
    [patientEmail, patientName, patientOwner?.address, patientPhone],
  );

  const methods = useForm<OrderFormValues>({
    mode: 'onBlur',
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    register,
    reset,
    watch,
    setValue,
    errors,
    setError,
    clearError,
    getValues,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lines',
  });

  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(
    null,
  );
  const [catalogOptions, setCatalogOptions] = useState<
    Record<number, CatalogOption[]>
  >({});
  const [lastSearchTerm, setLastSearchTerm] = useState<string>('');
  const [lineError, setLineError] = useState<string | null>(null);
  const [missingSupplierIndexes, setMissingSupplierIndexes] = useState<
    number[]
  >([]);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);

  const [
    fetchCatalog,
    { loading: loadingCatalog },
  ] = useLazyQuery<
    SearchCatalogProductsQuery,
    SearchCatalogProductsQueryVariables
  >(SEARCH_CATALOG_PRODUCTS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (activeSearchIndex === null) {
        return;
      }

      const rawItems = (data?.searchCatalogProducts || []).filter(
        Boolean,
      ) as CatalogProduct[];

      const options: CatalogOption[] =
        rawItems.map((item, idx) => {
          const optionId = `${item.itemNumber || 'NO_ITEM'}-${item.uom || 'NO_UOM'}-${idx}`;

          const labelParts: (string | null | undefined)[] = [
            item?.supplierItemNumber,
            item?.shortDescription,
            item?.uom
              ? `${item.uom}${item.uomToEach ? ` (${item.uomToEach})` : ''}`
              : null,
          ];

          const label = labelParts.filter(Boolean).join(' • ');

          const option: CatalogOption = {
            ...(item as CatalogProduct),
            label,
            optionId,
          };

          return option;
        }) || [];

      setCatalogOptions(prev => ({
        ...prev,
        [activeSearchIndex]: options,
      }));
    },
  });

  const [placeOrder, { loading: placingOrder }] = useMutationCustom<
    PlaceOrderMutation,
    PlaceOrderMutationVariables
  >({
    api: PLACE_ORDER,
    textSuccess: 'Order placed successfully',
    callbackSuccess: () => {
      onClose();
      reset(defaultValues);
      setMissingSupplierIndexes([]);
      setLineError(null);
      if (onOrderPlaced) {
        onOrderPlaced();
      }
    },
  });

  const {
    data: sftpData,
    loading: loadingSftpSettings,
  } = useQueryCustom<SftpSettingsQuery, SftpSettingsQueryVariables>({
    api: SFTP_SETTINGS,
    variables: { organizationId },
    skip: !organizationId,
  });

  const surveyResultId = surveyResult?._id
    ? String(surveyResult._id)
    : undefined;

  const {
    data: existingOrdersData,
    loading: loadingExistingOrders,
  } = useQueryCustom<EdiOrdersQuery, EdiOrdersQueryVariables>({
    api: EDI_ORDERS,
    variables: {
      params: {
        organizationId,
        surveyResultId,
      },
    },
    skip: !open || !organizationId || !surveyResultId,
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setCatalogOptions({});
      setLineError(null);
      setMissingSupplierIndexes([]);
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultValues]);

  useEffect(() => {
    if (!open) {
      setIsStatusDialogOpen(false);
    }
  }, [open]);

  useEffect(() => {
    fields.forEach((_, index) => {
      register({ name: `lines[${index}].supplierItem` });
      register({ name: `lines[${index}].buyerItem` });
    });
  }, [fields, register]);

  const handleAddLine = () => {
    append({ ...defaultLine });
  };

  const handleRemoveLine = (index: number) => {
    remove(index);
    setCatalogOptions(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setMissingSupplierIndexes(prev => {
      const next = prev
        .filter(item => item !== index)
        .map(item => (item > index ? item - 1 : item));
      if (next.length === 0) {
        setLineError(null);
      }
      return next;
    });
  };

  const handleSearchCatalog = useCallback(
    (index: number, term: string) => {
      if (!organizationId) {
        return;
      }
      setActiveSearchIndex(index);
      setLastSearchTerm(term);
      fetchCatalog({
        variables: {
          orgId: organizationId,
          term,
          limit: 20,
        },
      });
    },
    [fetchCatalog, organizationId],
  );

  const handleSelectCatalog = useCallback(
    (index: number, value: CatalogOption | string | null) => {
      const supplierName =
        typeof value === 'string' ? value : value?.supplierItemNumber;

      setValue(
        `lines[${index}].catalogSelection`,
        value || null,
        true,
      );
      setValue(
        `lines[${index}].supplierItem`,
        supplierName ? supplierName.trim() : '',
        true,
      );

      if (typeof value !== 'string' && value) {
        setValue(
          `lines[${index}].buyerItem`,
          value.itemNumber ? value.itemNumber.trim() : '',
          true,
        );
      } else {
        setValue(`lines[${index}].buyerItem`, '', true);
      }

      if (supplierName) {
        clearError(`lines[${index}].supplierItem`);
        setMissingSupplierIndexes(prev =>
          prev.filter(item => item !== index),
        );
      }
    },
    [clearError, setValue],
  );

  const lines = watch('lines');
  const shippingMode = watch('shippingMode');
  const clinicShipToCode = watch('clinicShipToCode');

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
    [],
  );

  const totalAmount = useMemo(() => {
    if (!lines || lines.length === 0) {
      return 0;
    }
    const total = lines.reduce((acc, line) => {
      const selectedCatalog =
        line?.catalogSelection && typeof line.catalogSelection === 'object'
          ? (line.catalogSelection as CatalogOption)
          : null;
      const cost =
        selectedCatalog?.cost !== undefined && selectedCatalog?.cost !== null
          ? Number(selectedCatalog.cost)
          : 0;
      const rawQty =
        typeof line?.requestedEach === 'string'
          ? Number(line.requestedEach)
          : Number(line?.requestedEach || 0);
      const quantity =
        Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 0;
      const lineTotal = cost * quantity;
      return acc + lineTotal;
    }, 0);
    return total;
  }, [lines]);

  const getUnitTypeLabel = useCallback(
    (uom?: string | null, uomToEach?: number | null) => {
      if (!uom) {
        return '—';
      }
      const normalized = uom.trim().toUpperCase();
      let label = normalized;
      if (normalized === 'EA') {
        label = 'Each';
      } else if (normalized === 'BX') {
        label = 'Box';
      }
      if (normalized === 'BX' && uomToEach) {
        return `${label} (${uomToEach} each)`;
      }
      return label;
    },
    [],
  );

  const existingOrder = useMemo(
    () => existingOrdersData?.ediOrders?.nodes?.[0] || null,
    [existingOrdersData],
  );

  const derivedSurveyResultId = useMemo(() => {
    if (surveyResult?._id) {
      return String(surveyResult._id);
    }
    const embeddedId =
      surveyResult?.patientNote?.surveyResult?.surveyResultId;
    return embeddedId ? String(embeddedId) : undefined;
  }, [surveyResult]);

  const derivedPatientId = useMemo(() => {
    if (patient?._id) {
      return String(patient._id);
    }
    const rawData = surveyResult?.data;
    if (!rawData) {
      return undefined;
    }
    let parsed: any = rawData;
    if (typeof rawData === 'string') {
      try {
        parsed = JSON.parse(rawData);
      } catch (err) {
        parsed = undefined;
      }
    }
    if (!parsed) {
      return undefined;
    }
    const candidates = [
      parsed.patientId,
      parsed.patient?.id,
      parsed.patient?._id,
      parsed.patient?.patientId,
      parsed.patient?._id?.$oid,
      parsed.patient?._id?.toString?.(),
      parsed.patient?.id?.$oid,
    ].filter(Boolean);
    if (candidates.length > 0) {
      return String(candidates[0]);
    }
    return undefined;
  }, [patient, surveyResult]);

  const {
    data: inboundDocsData,
    loading: loadingInboundDocs,
  } = useQueryCustom<
    InboundDocsQueryResult,
    { orgId: string; po?: string | null; type?: InboundDocType | null }
  >({
    api: EDI_INBOUND_DOCS,
    variables: {
      orgId: organizationId,
      po: existingOrder?.poNumber || undefined,
    },
    skip: !open || !organizationId || !existingOrder?.poNumber,
  });

  const inboundDocs = existingOrder
    ? inboundDocsData?.ediInboundDocs || []
    : [];

  useEffect(() => {
    if (!existingOrder) {
      setIsStatusDialogOpen(false);
    }
  }, [existingOrder]);

  const previousShippingModeRef = useRef<ShippingMode | null>(null);
  const patientShipToSnapshotRef = useRef<OrderFormValues['shipTo'] | null>(
    null,
  );

  const sftpSettings = sftpData?.sftpSettings || null;
  const shipToProfiles = useMemo(
    () => sftpSettings?.shipToProfiles || [],
    [sftpSettings],
  );
  const defaultClinicProfile = useMemo(
    () =>
      shipToProfiles.find(profile => profile?.isDefault) ||
      shipToProfiles[0],
    [shipToProfiles],
  );
  const selectedClinicProfile = useMemo(() => {
    if (!clinicShipToCode) {
      return defaultClinicProfile;
    }
    return (
      shipToProfiles.find(profile => profile?.code === clinicShipToCode) ||
      defaultClinicProfile
    );
  }, [clinicShipToCode, defaultClinicProfile, shipToProfiles]);
  const billToAccount = sftpSettings?.billToAccount;

  useEffect(() => {
    const previousMode = previousShippingModeRef.current;
    if (previousMode !== shippingMode) {
      if (shippingMode === ShippingMode.Clinic) {
        const source =
          (getValues('shipTo') as OrderFormValues['shipTo']) ||
          defaultValues.shipTo;
        patientShipToSnapshotRef.current = {
          name: source?.name || '',
          account: source?.account,
          addr1: source?.addr1,
          city: source?.city,
          state: source?.state,
          zip: source?.zip,
          phone: source?.phone,
          email: source?.email,
        };
      } else if (previousMode === ShippingMode.Clinic) {
        const snapshot =
          patientShipToSnapshotRef.current || defaultValues.shipTo;
        setValue('shipTo.name', snapshot?.name || '', false);
        setValue('shipTo.account', snapshot?.account || '', false);
        setValue('shipTo.addr1', snapshot?.addr1 || '', false);
        setValue('shipTo.city', snapshot?.city || '', false);
        setValue('shipTo.state', snapshot?.state || '', false);
        setValue('shipTo.zip', snapshot?.zip || '', false);
        setValue('shipTo.phone', snapshot?.phone || '', false);
        setValue('shipTo.email', snapshot?.email || '', false);
        patientShipToSnapshotRef.current = null;
      }
      previousShippingModeRef.current = shippingMode;
    }
  }, [defaultValues.shipTo, getValues, setValue, shippingMode]);

  useEffect(() => {
    if (
      shippingMode === ShippingMode.Clinic &&
      !clinicShipToCode &&
      defaultClinicProfile?.code
    ) {
      setValue('clinicShipToCode', defaultClinicProfile.code, false);
    }
  }, [clinicShipToCode, defaultClinicProfile, setValue, shippingMode]);

  useEffect(() => {
    if (shippingMode !== ShippingMode.Clinic) {
      return;
    }
    if (!selectedClinicProfile) {
      return;
    }
    setValue('shipTo.name', selectedClinicProfile.name || '', false);
    setValue('shipTo.addr1', selectedClinicProfile.addr1 || '', false);
    setValue('shipTo.city', selectedClinicProfile.city || '', false);
    setValue('shipTo.state', selectedClinicProfile.state || '', false);
    setValue('shipTo.zip', selectedClinicProfile.zip || '', false);
    setValue('shipTo.account', billToAccount || '', false);
  }, [billToAccount, selectedClinicProfile, setValue, shippingMode]);

  useEffect(() => {
    if (clinicShipToCode) {
      clearError('clinicShipToCode');
    }
  }, [clinicShipToCode, clearError]);

  useEffect(() => {
    if (shippingMode !== ShippingMode.Clinic) {
      clearError('clinicShipToCode');
    }
  }, [clearError, shippingMode]);

  useEffect(() => {
    if (shippingMode === ShippingMode.Patient) {
      setValue('shipTo.account', billToAccount || '', false);
    }
  }, [billToAccount, setValue, shippingMode]);

  const getSelectedCatalog = (line: OrderLineForm) => {
    if (line.catalogSelection && typeof line.catalogSelection === 'object') {
      return line.catalogSelection as CatalogOption;
    }
    return null;
  };

  const resolveSupplierItem = (line: OrderLineForm) => {
    const catalog = getSelectedCatalog(line);
    const fromCatalog = normalizeValue(catalog?.supplierItemNumber);
    if (fromCatalog) {
      return fromCatalog;
    }
    if (typeof line.catalogSelection === 'string') {
      const fromFreeSolo = normalizeValue(line.catalogSelection);
      if (fromFreeSolo) {
        return fromFreeSolo;
      }
    }
    return normalizeValue(line.supplierItem);
  };

  const resolveBuyerItem = (line: OrderLineForm) => {
    const catalog = getSelectedCatalog(line);
    const fromCatalog = normalizeValue(catalog?.itemNumber);
    if (fromCatalog) {
      return fromCatalog;
    }
    return normalizeValue(line.buyerItem);
  };

  const onSubmit = (formValues: OrderFormValues) => {
    if (!organizationId) {
      setError('shipTo.name', {
        type: 'manual',
        message: 'Organization context missing',
      });
      return;
    }

    const missingSupplierIdx = formValues.lines
      .map((line, idx) => (resolveSupplierItem(line) ? -1 : idx))
      .filter(idx => idx >= 0);

    if (missingSupplierIdx.length > 0) {
      setMissingSupplierIndexes(missingSupplierIdx);
      setLineError(
        'Add at least one catalog line with supplier item and quantity greater than zero',
      );
      return;
    }

    const preparedLines: PlaceOrderLineInput[] = formValues.lines
      .map(line => {
        const supplierItem = resolveSupplierItem(line);
        const buyerItem = resolveBuyerItem(line);
        const requestedEach =
          typeof line.requestedEach === 'string'
            ? Number(line.requestedEach)
            : line.requestedEach;

        const selectedCatalog = getSelectedCatalog(line);
        const uom =
          selectedCatalog?.uom?.trim().toUpperCase() || 'EA';

        const preparedLine: PlaceOrderLineInput = {
          supplierItem: supplierItem ? supplierItem.trim() : '',
          buyerItem: buyerItem ? buyerItem.trim() : undefined,
          uom,
          requestedEach,
        };

        return preparedLine;
      })
      .filter(
        line =>
          normalizeValue(line.supplierItem) &&
          line.requestedEach !== null &&
          line.requestedEach !== undefined &&
          !Number.isNaN(line.requestedEach) &&
          line.requestedEach > 0,
      );

    if (preparedLines.length === 0) {
      setLineError(
        'Add at least one catalog line with supplier item and quantity greater than zero',
      );
      return;
    }

    setLineError(null);
    setMissingSupplierIndexes([]);

    const isClinicMode = formValues.shippingMode === ShippingMode.Clinic;
    const clinicProfile =
      shipToProfiles.find(
        profile => profile?.code === formValues.clinicShipToCode,
      ) || defaultClinicProfile;

    if (isClinicMode && !clinicProfile) {
      setError('clinicShipToCode', {
        type: 'manual',
        message: 'Select a clinic destination',
      });
      return;
    }

    const shipToPayload = isClinicMode
      ? {
        name:
          normalizeValue(clinicProfile?.name) ||
          clinicProfile?.code ||
          '',
        account: normalizeValue(billToAccount),
        addr1: normalizeValue(clinicProfile?.addr1),
        city: normalizeValue(clinicProfile?.city),
        state: normalizeValue(clinicProfile?.state),
        zip: normalizeValue(clinicProfile?.zip),
        phone: undefined,
        email: undefined,
      }
      : {
        name: formValues.shipTo.name.trim(),
        account: normalizeValue(billToAccount),
        addr1: normalizeValue(formValues.shipTo.addr1),
        city: normalizeValue(formValues.shipTo.city),
        state: normalizeValue(formValues.shipTo.state),
        zip: normalizeValue(formValues.shipTo.zip),
        phone: normalizeValue(formValues.shipTo.phone),
        email: normalizeValue(formValues.shipTo.email),
      };

    const params: PlaceOrderInput = {
      organizationId,
      patientId: derivedPatientId,
      surveyResultId: derivedSurveyResultId,
      shippingMode: formValues.shippingMode,
      shipTo: shipToPayload,
      lines: preparedLines.map(line => ({
        supplierItem: line.supplierItem,
        buyerItem: line.buyerItem,
        uom: line.uom,
        requestedEach: line.requestedEach,
      })),
    };

    placeOrder({
      variables: {
        params,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitleClose
        title={
          <Box display="flex" alignItems="center" gridGap={8}>
            <ShoppingCartIcon fontSize="small" />
            <Typography variant="h6" className="text-tranform-none">
              {`Place Order${surveyName ? ` • ${surveyName}` : ''}`}
            </Typography>
          </Box>
        }
        onClose={onClose}
      />
      <DialogContent dividers>
        {existingOrder ? (
          <Box display="grid" gridGap={16}>
            <Typography variant="subtitle1" className="font-weight-bold">
              An order has already been submitted for this survey.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Review the current order status or contact support if changes are
              required.
            </Typography>
          </Box>
        ) : (
          <form>
            <Box display="grid" gridGap={16}>
              <Typography variant="subtitle1" className="font-weight-bold">
                Shipping Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextfieldSelect
                    control={control}
                    name="shippingMode"
                    label="Shipping Mode"
                    required
                    defaultValue={defaultValues.shippingMode}
                  >
                    <MenuItem value={ShippingMode.Patient}>Patient</MenuItem>
                    <MenuItem value={ShippingMode.Clinic}>Clinic</MenuItem>
                  </TextfieldSelect>
                </Grid>
                {shippingMode === ShippingMode.Clinic ? (
                  <Grid item xs={12} md={8}>
                    <TextfieldSelect
                      control={control}
                      name="clinicShipToCode"
                      label="Clinic Destination"
                      required
                      defaultValue={defaultClinicProfile?.code || ''}
                      disabled={
                        loadingSftpSettings || shipToProfiles.length === 0
                      }
                      error={Boolean(errors?.clinicShipToCode)}
                      helperText={
                        errors?.clinicShipToCode?.message ||
                        (loadingSftpSettings
                          ? 'Loading clinic profiles...'
                          : shipToProfiles.length === 0
                            ? 'Configure clinic ship-to profiles in SFTP settings.'
                            : undefined)
                      }
                    >
                      {shipToProfiles.map((profile, idx) => {
                        const value = profile?.code || '';
                        const label =
                          profile?.name || profile?.code || 'Unnamed clinic';
                        return (
                          <MenuItem key={`${value}-${idx}`} value={value}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </TextfieldSelect>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} md={8}>
                      <TextField
                        name="shipTo.name"
                        label="Ship To Name"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputRef={register({
                          required: 'Ship to name is required',
                        })}
                        error={Boolean(errors?.shipTo?.name)}
                        helperText={errors?.shipTo?.name?.message}
                        defaultValue={defaultValues.shipTo.name}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        name="shipTo.addr1"
                        label="Address"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputRef={register({
                          required: 'Address is required',
                        })}
                        error={Boolean(errors?.shipTo?.addr1)}
                        helperText={errors?.shipTo?.addr1?.message}
                        defaultValue={defaultValues.shipTo.addr1}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="shipTo.city"
                        label="City"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputRef={register()}
                        defaultValue={defaultValues.shipTo.city}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        name="shipTo.state"
                        label="State"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputRef={register({
                          required: 'State is required',
                        })}
                        error={Boolean(errors?.shipTo?.state)}
                        helperText={errors?.shipTo?.state?.message}
                        defaultValue={defaultValues.shipTo.state}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        name="shipTo.zip"
                        label="ZIP"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputRef={register({
                          required: 'ZIP is required',
                        })}
                        error={Boolean(errors?.shipTo?.zip)}
                        helperText={errors?.shipTo?.zip?.message}
                        defaultValue={defaultValues.shipTo.zip}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        name="shipTo.phone"
                        label="Phone"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputRef={register()}
                        defaultValue={defaultValues.shipTo.phone}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="shipTo.email"
                        label="Email"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputRef={register()}
                        defaultValue={defaultValues.shipTo.email}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle1" className="font-weight-bold">
                  Order Lines
                </Typography>
                <Box display="flex" alignItems="center" gridGap={16}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Amount:{' '}
                    <Box
                      component="span"
                      color="textPrimary"
                      fontWeight={600}
                    >
                      {currencyFormatter.format(totalAmount || 0)}
                    </Box>
                  </Typography>
                  <IconButton
                    color="primary"
                    onClick={handleAddLine}
                    aria-label="Add line"
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Box>
              </Box>
              {lineError && (
                <Typography color="error" variant="body2">
                  {lineError}
                </Typography>
              )}
              <Box display="grid" gridGap={16}>
                {fields.map((field, index) => {
                  const catalogSelection = lines?.[index]?.catalogSelection;
                  const selectedCatalog =
                    catalogSelection && typeof catalogSelection === 'object'
                      ? (catalogSelection as CatalogOption)
                      : null;

                  return (
                    <Box
                      key={field.id}
                      border={1}
                      borderColor="grey.300"
                      borderRadius={8}
                      p={2}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={7}>
                              <AutoCompleteLoadData<CatalogOption>
                                control={control}
                                options={catalogOptions[index] || []}
                                nameOption="label"
                                name={`lines[${index}].catalogSelection`}
                                label="Supplier Item"
                                idOption="optionId"
                                placeholder="Search by supplier item or description"
                                freeSolo
                                small
                                loadList={
                                  loadingCatalog &&
                                  activeSearchIndex === index
                                }
                                defaultValue={
                                  (lines?.[index]
                                    ?.catalogSelection as CatalogOption) || null
                                }
                                methods={methods as any}
                                callbackHandleText={text => {
                                  handleSearchCatalog(index, text);
                                }}
                                callbackChangeValue={(_, newValue) => {
                                  handleSelectCatalog(index, newValue);
                                }}
                                callbackRenderOption={(
                                  option: CatalogOption,
                                ) => (
                                  <Box display="flex" flexDirection="column">
                                    <Typography variant="body2">
                                      {option.supplierItemNumber}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {option.shortDescription}
                                      {option.uom
                                        ? ` • ${option.uom}${option.uomToEach
                                          ? ` (${option.uomToEach})`
                                          : ''
                                        }`
                                        : ''}
                                    </Typography>
                                  </Box>
                                )}
                              />
                              {missingSupplierIndexes.includes(index) && (
                                <Typography
                                  color="error"
                                  variant="caption"
                                >
                                  Supplier item is required
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={10} md={3}>
                              <TextField
                                name={`lines[${index}].requestedEach`}
                                label="Quantity"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputRef={register({
                                  required: 'Quantity is required',
                                  min: {
                                    value: 1,
                                    message:
                                      'Quantity must be at least 1',
                                  },
                                })}
                                error={Boolean(
                                  errors?.lines?.[index]?.requestedEach,
                                )}
                                helperText={
                                  (errors?.lines?.[index]
                                    ?.requestedEach as any)?.message
                                }
                              />
                            </Grid>
                            <Grid item xs={2} md={2}>
                              <IconButton
                                aria-label="Remove line"
                                onClick={() => handleRemoveLine(index)}
                                disabled={fields.length === 1}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          {selectedCatalog ? (
                            <Box
                              display="flex"
                              flexDirection="column"
                              gridGap={12}
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Supplier Item
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textPrimary"
                                  >
                                    {selectedCatalog.supplierItemNumber}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Item Number
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textPrimary"
                                  >
                                    {selectedCatalog.itemNumber || '—'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Unit Price
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textPrimary"
                                  >
                                    {selectedCatalog.cost !== undefined &&
                                      selectedCatalog.cost !== null
                                      ? currencyFormatter.format(
                                        Number(selectedCatalog.cost),
                                      )
                                      : '—'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Unit Type
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textPrimary"
                                  >
                                    {getUnitTypeLabel(
                                      selectedCatalog.uom,
                                      selectedCatalog.uomToEach,
                                    )}
                                  </Typography>
                                </Grid>
                              </Grid>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Description
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textPrimary"
                                  >
                                    {selectedCatalog.shortDescription ||
                                      '—'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >
                              Select a supplier item to view its details.
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <DialogButton isCancel onClickButton={onClose} />
        {existingOrder ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<VisibilityIcon />}
            onClick={() => setIsStatusDialogOpen(true)}
            disabled={loadingExistingOrders}
          >
            View Order Status
          </Button>
        ) : (
          <DialogButton
            loading={placingOrder}
            onClickButton={handleSubmit(onSubmit)}
            disabled={loadingExistingOrders}
          />
        )}
      </DialogActions>
      <OrderStatusDialog
        open={isStatusDialogOpen}
        onClose={() => setIsStatusDialogOpen(false)}
        order={existingOrder}
        surveyResult={surveyResult}
        surveyName={surveyName}
        docs={inboundDocs}
        loadingDocs={loadingInboundDocs}
      />
    </Dialog>
  );
};

export default React.memo(PlaceOrderDialog);
